import { useState } from 'react';
import { playerDisplayName, type AuthResult, type Handicap, type RedCount } from '@aiscoreboard/contracts';
import { FrameStepper } from '../components/FrameStepper';
import { RedCountStepper } from '../components/RedCountStepper';
import { FullscreenToggle } from '../components/FullscreenToggle';
import { HandicapField } from '../components/HandicapField';
import { BrandMark } from '../components/BrandMark';
import { SeatCard } from '../components/SeatCard';
import { TimeLimitField } from '../components/TimeLimitField';
import type { Seat } from '../lib/accounts';
import { accentOf } from '../lib/accent';
import { useT } from '../lib/i18n';
import type { SeatSession } from '../lib/useSeats';

interface Props {
  seats: [SeatSession | null, SeatSession | null];
  bestOf: number;
  onBestOfChange: (n: number) => void;
  redCount: RedCount;
  onRedCountChange: (n: RedCount) => void;
  timeLimitMinutes: number | null;
  onTimeLimitChange: (minutes: number | null) => void;
  handicap: Handicap | null;
  onHandicapChange: (handicap: Handicap | null) => void;
  onSignIn: (seat: Seat) => void;
  onSignOut: (seat: Seat) => void;
  onSettings: (seat: Seat) => void;
  /** A phone claimed this seat's QR code and signed in there. */
  onPaired: (seat: Seat, auth: AuthResult) => void;
  /** Per-seat refusals, e.g. an account already sitting in the other seat. */
  notices?: [string | null, string | null];
  onStart: (
    players: [string, string],
    playerIds: [string | null, string | null],
    countries: [string | null, string | null],
  ) => void;
  onBack: () => void;
  starting: boolean;
  error?: string | null;
}

export function MatchSetupScreen({
  seats,
  bestOf,
  onBestOfChange,
  redCount,
  onRedCountChange,
  timeLimitMinutes,
  onTimeLimitChange,
  handicap,
  onHandicapChange,
  onSignIn,
  onSignOut,
  onSettings,
  onPaired,
  onStart,
  onBack,
  starting,
  error,
  notices,
}: Props) {
  const t = useT();
  // The name an unclaimed seat starts with. "Guest 1" rather than "Player 1":
  // the card is already headed with the seat, so repeating it in the name
  // field said the same word twice and made a placeholder look like a filled
  // -in name. Signing in replaces it; typing over it is one tap.
  const [guests, setGuests] = useState<[string, string]>([
    t('seat.guestNumbered', { n: 1 }),
    t('seat.guestNumbered', { n: 2 }),
  ]);
  const [guestCountries, setGuestCountries] = useState<[string, string]>(['DE', 'DE']);

  const nameFor = (seat: Seat): string => {
    const session = seats[seat];
    return session ? playerDisplayName(session.player) : guests[seat].trim();
  };

  const ready =
    nameFor(0) !== '' &&
    nameFor(1) !== '' &&
    (seats[0] !== null || guestCountries[0] !== '') &&
    (seats[1] !== null || guestCountries[1] !== '');
  const rated = seats[0] !== null && seats[1] !== null;

  // A function that is *called*, not a component that is rendered. Declaring a
  // component inside the render body gives it a new identity every pass, so
  // React unmounts and remounts it — which here would mint a fresh pairing
  // code, and invalidate the one already on screen, on every keystroke in the
  // guest-name field.
  const seatCard = (seat: Seat) => (
    <SeatCard
      key={seat}
      label={t('seat.player', { n: seat + 1 })}
      accent={accentOf(seat)}
      session={seats[seat]}
      guestName={guests[seat]}
      onGuestNameChange={(name) =>
        setGuests((prev) => {
          const next: [string, string] = [...prev];
          next[seat] = name;
          return next;
        })
      }
      guestCountry={guestCountries[seat]}
      onGuestCountryChange={(country) =>
        setGuestCountries((prev) => {
          const next: [string, string] = [...prev];
          next[seat] = country;
          return next;
        })
      }
      onSignIn={() => onSignIn(seat)}
      onSignOut={() => onSignOut(seat)}
      onSettings={() => onSettings(seat)}
      onPaired={(auth) => onPaired(seat, auth)}
      notice={notices?.[seat] ?? null}
    />
  );

  return (
    // Same screen grammar as scoring, but fully responsive to the device.
    <div className="board-viewport">
      <main className="board-shell setup-shell mx-auto flex w-full max-w-[min(140rem,95vw)] flex-col gap-2 p-2 sm:gap-3 sm:p-3">
        <header className="setup-header grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2 md:gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={onBack}
              className="btn-ghost shrink-0 px-3.5 py-2 text-[0.78rem] font-semibold tracking-[0.04em]"
            >
              ← {t('common.back')}
            </button>
            <h1 className="truncate text-xl font-black">{t('match.title')}</h1>
          </div>
          <BrandMark compact className="text-center" />
          <div className="flex min-w-0 items-center justify-end">
            <FullscreenToggle
              className="btn-ghost px-3 py-2 text-[0.72rem] font-bold uppercase tracking-[0.12em] text-white/85"
            />
          </div>
        </header>

        {/* CSS reorders the settings, not the seat components: changing
            viewport size must not replace an active pairing code. */}
        <div className="setup-body flex flex-1">
          <div className="setup-grid w-full gap-2 sm:gap-3">
            {seatCard(0)}

            <div className="setup-settings flex w-full shrink-0 flex-col items-center">
              {/* The vertical gaps match the horizontal ones between this
                  column and the seat cards, so the three panels read as part
                  of the same grid rather than as a stack of their own. */}
              <div className="setup-fields flex w-full flex-col gap-2 sm:gap-3">
                <div className="setup-setting-card panel">
                  <FrameStepper value={bestOf} onChange={onBestOfChange} />
                </div>

                <div className="setup-setting-card setup-reds panel">
                  <RedCountStepper value={redCount} onChange={onRedCountChange} />
                </div>

                <div className="setup-setting-card setup-handicap panel">
                  <HandicapField value={handicap} onChange={onHandicapChange} />
                </div>

                <div className="setup-setting-card panel">
                  <TimeLimitField value={timeLimitMinutes} onChange={onTimeLimitChange} dense showHint={false} />
                </div>
              </div>
            </div>

            {seatCard(1)}
          </div>
        </div>

        <div className="flex min-h-[7rem] shrink-0 flex-col justify-center gap-2 py-2">
          <p className="shrink-0 text-center text-xs text-white/50">
            {rated ? t('match.rated') : t('match.unrated')}
          </p>

          <button
            type="button"
            disabled={!ready || starting}
            onClick={() =>
              onStart(
                [nameFor(0), nameFor(1)],
                [seats[0]?.player.id ?? null, seats[1]?.player.id ?? null],
                [
                  seats[0]?.player.country ?? guestCountries[0] ?? null,
                  seats[1]?.player.country ?? guestCountries[1] ?? null,
                ],
              )
            }
            className="btn shrink-0 bg-white py-4 text-xl text-hud-bg"
          >
            {starting ? t('match.starting') : t('match.start')}
          </button>

          {error && <p className="shrink-0 text-center text-sm text-ball-red">{error}</p>}
        </div>
      </main>
    </div>
  );
}
